import type { Department } from "@/lib/auth-context";

export const DEPT_CAPS = ["view", "edit", "approve_corrections"] as const;
export type DeptCap = (typeof DEPT_CAPS)[number];

export const DEPT_CAP_LABELS: Record<DeptCap, string> = {
  view: "View",
  edit: "Edit",
  approve_corrections: "Approve corrections",
};

export const DEPT_CAP_DESCRIPTIONS: Record<DeptCap, string> = {
  view: "See submissions and tasks for this department",
  edit: "Modify submissions and tasks for this department",
  approve_corrections: "Approve or reject correction requests",
};

export const WORKSPACE_CAPS = [
  "view_overview",
  "view_activity_feed",
  "search_users",
  "view_audit_log",
  "creatives_review",
  "task_assign",
  "send_notifications",
] as const;
export type WorkspaceCap = (typeof WORKSPACE_CAPS)[number];

export const WORKSPACE_CAP_LABELS: Record<WorkspaceCap, string> = {
  view_overview: "View workspace overview",
  view_activity_feed: "View live activity feed",
  search_users: "Search users globally",
  view_audit_log: "View audit log",
  creatives_review: "Review creatives (Editor + Designer)",
  task_assign: "Assign tasks to users",
  send_notifications: "Send notifications",
};

export const WORKSPACE_CAP_DESCRIPTIONS: Record<WorkspaceCap, string> = {
  view_overview: "Access /team/overview with cross-department KPIs",
  view_activity_feed: "Access /team/activity live feed of submissions",
  search_users: "Access /team/users to search and view any user",
  view_audit_log: "View the audit log of admin/moderator actions",
  creatives_review: "Approve, request changes, or flag corrections on creative submissions",
  task_assign: "Access /admin/assign-task to create and assign tasks to users",
  send_notifications: "Push announcements to all/department/individual recipients",
};

/**
 * Section-visibility keys (per-moderator sidebar/section toggles).
 * Menu-hide + route-guard only. Default (undefined) = ON, so existing
 * moderators keep every section they had before this feature shipped.
 */
export const SECTION_KEYS = [
  { key: "section_work_update", label: "Work Update", paths: ["/update-task"] },
  { key: "section_work_boards", label: "Work Boards", paths: ["/boards"] },
  { key: "section_calendar", label: "Calendar", paths: ["/calendar"] },
  { key: "section_creatives", label: "Creatives", paths: ["/creatives"] },
  { key: "section_creative_library", label: "Creative Library", paths: ["/creative-library"] },
  { key: "section_today_work", label: "Today's Work", paths: ["/today-tasks"] },
  { key: "section_by_department", label: "By Department", paths: ["/team/departments"] },
  { key: "section_by_employee", label: "By Employee", paths: ["/team/employees"] },
  { key: "section_sops", label: "SOPs", paths: ["/sops"] },
  { key: "section_chat", label: "Chat", paths: ["/chat"] },
  { key: "section_vault", label: "The Vault", paths: ["/vault"] },
  {
    key: "section_send_notifications",
    label: "Send Notifications",
    paths: ["/notifications/send", "/notifications/history"],
  },
  { key: "section_ads_performance", label: "Ads Performance", paths: ["/ads-performance"] },
  {
    key: "section_creative_production",
    label: "Creative Production",
    paths: ["/creative-production"],
  },
] as const;

export type SectionKey = (typeof SECTION_KEYS)[number]["key"];

/**
 * Allow-list of every valid moderator permission key — used by the server
 * validator to reject typos / unknown keys before writing to the DB.
 */
export const MODERATOR_PERMISSION_KEYS = [
  ...WORKSPACE_CAPS,
  "departments",
  "user_management",
  "lookups",
  "globals",
  "scope_own_department",
  ...SECTION_KEYS.map((s) => s.key),
] as const;
export type ModeratorPermissionKey = (typeof MODERATOR_PERMISSION_KEYS)[number];


export const USER_MGMT_CAPS = [
  "create_users",
  "edit_users",
  "reset_passwords",
  "suspend_users",
] as const;
export type UserMgmtCap = (typeof USER_MGMT_CAPS)[number];

export const USER_MGMT_CAP_LABELS: Record<UserMgmtCap, string> = {
  create_users: "Create users",
  edit_users: "Edit user profiles",
  reset_passwords: "Reset passwords",
  suspend_users: "Suspend users",
};

export const LOOKUP_CAPS = ["edit_lookups"] as const;
export type LookupCap = (typeof LOOKUP_CAPS)[number];

export const LOOKUP_CAP_LABELS: Record<LookupCap, string> = {
  edit_lookups: "Edit lookup lists",
};

// Backwards-compat aliases (legacy code still imports these)
export const GLOBAL_CAPS = [...WORKSPACE_CAPS, ...LOOKUP_CAPS] as const;
export type GlobalCap = (typeof GLOBAL_CAPS)[number];
export const GLOBAL_CAP_LABELS: Record<GlobalCap, string> = {
  ...WORKSPACE_CAP_LABELS,
  ...LOOKUP_CAP_LABELS,
};

export interface ModeratorPermissions {
  view_overview?: boolean;
  view_activity_feed?: boolean;
  search_users?: boolean;
  view_audit_log?: boolean;
  creatives_review?: boolean;
  task_assign?: boolean;
  send_notifications?: boolean;
  departments?: Partial<Record<Department, Partial<Record<DeptCap, boolean>>>>;
  user_management?: Partial<Record<UserMgmtCap, boolean>>;
  lookups?: Partial<Record<LookupCap, boolean>>;
  /** Legacy bucket — still read by RLS via mod_can_global */
  globals?: Partial<Record<string, boolean>>;
  /** When true, team-visibility pages are UI-filtered to the moderator's own department. */
  scope_own_department?: boolean;
  // Section toggles (menu-hide + route-guard). Undefined = enabled.
  section_work_update?: boolean;
  section_work_boards?: boolean;
  section_calendar?: boolean;
  section_creatives?: boolean;
  section_creative_library?: boolean;
  section_today_work?: boolean;
  section_by_department?: boolean;
  section_by_employee?: boolean;
  section_sops?: boolean;
  section_chat?: boolean;
  section_vault?: boolean;
  section_send_notifications?: boolean;
  section_ads_performance?: boolean;
  section_creative_production?: boolean;

}

/** Section toggles default to ON when the key is absent (backward compat). */
export function modSectionEnabled(
  perms: ModeratorPermissions | null | undefined,
  key: SectionKey,
): boolean {
  const v = perms?.[key];
  return v === undefined || v === true;
}

/** Look up which section a given URL belongs to (exact match on registered paths). */
export function sectionForPath(pathname: string): SectionKey | null {
  for (const s of SECTION_KEYS) {
    for (const p of s.paths) {
      if (pathname === p || pathname.startsWith(p + "/")) return s.key;
    }
  }
  return null;
}


export function emptyPermissions(): ModeratorPermissions {
  return {
    view_overview: false,
    view_activity_feed: false,
    search_users: false,
    view_audit_log: false,
    creatives_review: false,
    task_assign: false,
    departments: {},
    user_management: {},
    lookups: {},
    globals: {},
  };
}

export function modCanDept(
  perms: ModeratorPermissions | null | undefined,
  dept: Department,
  cap: DeptCap,
): boolean {
  return Boolean(perms?.departments?.[dept]?.[cap]);
}

export function modCanWorkspace(
  perms: ModeratorPermissions | null | undefined,
  cap: WorkspaceCap,
): boolean {
  // Read both new top-level field and legacy globals bucket for compat
  return Boolean(perms?.[cap] || perms?.globals?.[cap]);
}

export function modCanGlobal(
  perms: ModeratorPermissions | null | undefined,
  cap: GlobalCap,
): boolean {
  if (cap === "edit_lookups") {
    return Boolean(perms?.lookups?.edit_lookups || perms?.globals?.edit_lookups);
  }
  return modCanWorkspace(perms, cap as WorkspaceCap);
}

export function countEnabled(perms: ModeratorPermissions | null | undefined): {
  enabled: number;
  total: number;
} {
  if (!perms) return { enabled: 0, total: 0 };
  let enabled = 0;
  let total = 0;
  for (const c of WORKSPACE_CAPS) {
    total++;
    if (perms[c]) enabled++;
  }
  // departments × caps
  // total is computed dynamically by caller passing dept list, but we approximate:
  return { enabled, total };
}
